import React, { useEffect, useRef, useState } from 'react';
import { motion, animate, useMotionValue, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { useTheme } from '@circuitry-ai/doc-ui';
import { KnowledgeGraphBackground } from './KnowledgeGraphBackground';

const PRIMARY = '#003A79';
const ACCENT = '#d4007e';
const SOFT_BG = '#F4F4F8';
const SOFT_BG_DARK = '#0D1117';
const CENTER_R = 58;
const ORBIT_NODE_R = 40;
const ORBIT_R = 148;
const ORBIT_DURATION_MS = 10000;
const EXPAND_DURATION_MS = 800;
const EXPAND_DELAY_MS = 0;
const SETTLE_MS = 180;
const INNER_DOT_R = 10;
const COMPANY_NAME = globalThis.location.hostname.split('.')[0] === 'dev' || globalThis.location.hostname.split('.')[0] === 'sandbox' || globalThis.location.hostname.split('.')[0] === 'app' || globalThis.location.hostname === 'localhost' ? 'circuitry.ai' : globalThis.location.hostname.split('.')[0];
const COMPANY_NAME_DISPLAY = COMPANY_NAME.charAt(0).toUpperCase() + COMPANY_NAME.slice(1);
const COMPANY_NAME_FONT_SIZE = (() => {
  const len = COMPANY_NAME.length;
  // Center node radius is 58, diameter 116. Max available width ~110.
  // Poppins characters average ~0.52 width-to-height ratio.
  const maxAvailableWidth = 106;
  const estimatedCharWidthFactor = 0.6;
  const size = maxAvailableWidth / (len * estimatedCharWidthFactor);
  return Math.max(10, Math.min(22, size));
})();

const NODES = [
  { key: 'agents', icon: 'material-symbols-light:graph-2', labelKey: 'Agents' },
  { key: 'journeys', icon: 'oui:nav-ai-flow', labelKey: 'Journeys' },
  { key: 'advisors', icon: 'solar:glasses-linear', labelKey: 'Advisors' },
  { key: 'analysts', icon: 'system-uicons:graph-box', labelKey: 'Analysts' },
] as const;

function polarToCart(cx: number, cy: number, r: number, deg: number) {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

const CENTER_STROKE_WIDTH = 2;

export function AgentCircuitLoader() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  // Theme-aware palette; light values unchanged.
  const primary = PRIMARY;
  const accent = ACCENT;
  const bg = isDark ? SOFT_BG_DARK : SOFT_BG;
  const centerFill = isDark ? '#161b22' : '#f3f4f6';
  const companyTextFill = isDark ? '#c4c4c4' : '#6b7280';
  const [orbitRadius, setOrbitRadius] = useState(0);
  const [outerNodeRadius, setOuterNodeRadius] = useState(0);
  const rotationMotion = useMotionValue(0);
  const counterRotateMotion = useTransform(rotationMotion, (v) => -v);
  const rafRef = useRef<number>(0);
  const startRef = useRef<number>(0);

  const cx = 200;
  const cy = 200;
  const phase2StartMs = EXPAND_DELAY_MS + EXPAND_DURATION_MS + SETTLE_MS;

  // Phase 1: inner circles come out from center (orbitRadius 0 → ORBIT_R)
  // Ensure the dots literally start at r=0 (from the very center point) instead of an initial offset
  useEffect(() => {
    const controls = animate(0, ORBIT_R, {
      duration: EXPAND_DURATION_MS / 1000,
      ease: [0.22, 0.61, 0.36, 1],
      onUpdate: (v) => setOrbitRadius(v),
    });
    return () => controls.stop();
  }, []);

  // Phase 2: after settle, grow outer circle and start revolving (same time)
  useEffect(() => {
    const id = setTimeout(() => {
      animate(0, ORBIT_NODE_R, {
        duration: 0.25,
        ease: [0.22, 0.61, 0.36, 1],
        onUpdate: (v) => setOuterNodeRadius(v),
      });
    }, phase2StartMs);
    return () => clearTimeout(id);
  }, []);

  // Rotation via motion value (no setState) for smooth 60fps revolution
  useEffect(() => {
    const step = (timestamp: number) => {
      if (!startRef.current) startRef.current = timestamp;
      const elapsed = timestamp - startRef.current;
      if (elapsed >= phase2StartMs) {
        const rotationElapsed = elapsed - phase2StartMs;
        rotationMotion.set((rotationElapsed / ORBIT_DURATION_MS) * 360);
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [phase2StartMs, rotationMotion]);

  return (
    <div
      className="relative flex h-screen w-full flex-col items-center justify-center overflow-hidden"
      style={{
        fontFamily: 'Poppins, sans-serif',
        background: bg,
      }}
    >

      {/* ========== Knowledge-graph layer: nodes + edges from edges toward center (above dynamic BG, below circuit) ========== */}
      <div className="absolute inset-0" style={{ zIndex: 1 }}>
        <KnowledgeGraphBackground isDark={isDark} />
      </div>

      {/* ========== Content: circuit + copy (above background) ========== */}
      <div
        className="relative z-10 flex flex-col items-center justify-center px-4 w-full h-[80vh] [--loader-icon-size:24px] sm:[--loader-icon-size:28px] lg:[--loader-icon-size:32px]"
      >
        <svg
          width="auto"
          height="100%"
          viewBox="0 0 400 420"
          className="flex-shrink-0 max-w-full"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden
        >
          <defs>
            <linearGradient id="center-glow" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={primary} stopOpacity={0.35} />
              <stop offset="100%" stopColor={accent} stopOpacity={0.22} />
            </linearGradient>
            <linearGradient id="line-grad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={primary} />
              <stop offset="100%" stopColor={accent} />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="2" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Central node: fill to inner edge of stroke so no gap, then gradient stroke */}
          <motion.circle
            cx={cx}
            cy={cy}
            r={CENTER_R - CENTER_STROKE_WIDTH / 2}
            fill={centerFill}
            initial={{ scale: 0, opacity: 0 }}
            animate={{
              scale: 1,
              opacity: 1,
              transition: { duration: 0.4, ease: [0.22, 0.61, 0.36, 1] },
            }}
          />
          <motion.circle
            cx={cx}
            cy={cy}
            r={CENTER_R}
            fill="none"
            stroke="url(#line-grad)"
            strokeWidth={CENTER_STROKE_WIDTH}
            initial={{ scale: 0, opacity: 0 }}
            animate={{
              scale: 1,
              opacity: 1,
              transition: { duration: 0.52, ease: [0.22, 0.61, 0.36, 1] },
            }}
          />
          {/* Company name centered in the center node */}
          <text
            x={cx}
            y={cy}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={companyTextFill}
            fontSize={COMPANY_NAME_FONT_SIZE}
            fontFamily="Poppins, sans-serif"
            fontWeight="500"
          >
            {COMPANY_NAME_DISPLAY}
          </text>
          {/* Logo: magenta icon (circuitry.ai only), positioned above-left of center */}
          {COMPANY_NAME === 'circuitry.ai' && (
            <g transform={`translate(${cx - 58}, ${cy - 35})`}>
              <svg viewBox="0 -40 145 92" width="115" height="50" preserveAspectRatio="xMidYMid meet">
                <g transform="translate(88, -36) scale(1.3)">
                  <g clipPath="url(#loader-logo-clip)">
                    <path d="M29.9602 26.7104C30.9432 26.7104 31.7402 25.9135 31.7402 24.9304C31.7402 23.9473 30.9432 23.1504 29.9602 23.1504C28.9771 23.1504 28.1802 23.9473 28.1802 24.9304C28.1802 25.9135 28.9771 26.7104 29.9602 26.7104Z" fill="#D4007E" />
                    <path d="M21.4898 33.3703C20.7898 32.6703 20.7898 31.5303 21.4898 30.8303L24.8598 27.4703C25.5598 26.7703 26.6998 26.7703 27.3998 27.4703C28.0998 28.1703 28.0998 29.3103 27.3998 30.0103L24.0298 33.3703C23.3298 34.0703 22.1898 34.0703 21.4898 33.3703Z" fill="#D4007E" />
                    <path d="M20.85 28.95C19.81 29.99 18.11 29.99 17.07 28.95C16.03 27.9 16.03 26.21 17.07 25.17L23.65 18.6C25.13 17.1 25.8 14.96 25.46 12.89C24.87 9.29999 21.46 6.85999 17.87 7.44999C14.28 8.03999 11.84 11.45 12.43 15.04C12.55 15.79 12.96 17.24 14.28 18.56C14.56 18.84 15.86 20.16 15.86 20.16C16.88 21.21 16.87 22.89 15.84 23.92C14.81 24.95 13.1 24.96 12.06 23.92C12.03 23.89 10.73 22.57 10.5 22.34C8.73999 20.57 7.54999 18.29 7.15999 15.91C6.08999 9.39999 10.5 3.22999 17.01 2.15999C23.51 1.08999 29.68 5.49999 30.75 12.01C31.37 15.77 30.14 19.64 27.46 22.36L27.44 22.38L20.85 28.95Z" fill="#D4007E" />
                  </g>
                </g>
              </svg>
            </g>
          )}

          {/* Phase 1: inner circles only come out from center (no rotation). Hidden once phase 2 starts. */}
          {outerNodeRadius === 0 && (
            <g transform={`translate(${cx}, ${cy})`}>
              {[0, 90, 180, 270].map((deg) => {
                const pos = polarToCart(0, 0, orbitRadius, deg);
                // Start drawing the line from exactly the center point (0, 0) out to the current orbit pos
                const spokePath = `M 0 0 L ${pos.x} ${pos.y}`;
                return (
                  <g key={`p1-spoke-${deg}`}>
                    <motion.path
                      d={spokePath}
                      fill="none"
                      stroke="url(#line-grad)"
                      strokeWidth="2"
                      strokeLinecap="round"
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={{
                        pathLength: 1,
                        opacity: 0.5,
                        transition: { duration: 0.4, ease: [0.22, 0.61, 0.36, 1] },
                      }}
                      style={{ filter: 'url(#glow)' }}
                    />
                    {/* Inner circle only – starts at scale 0 and expands out */}
                    <motion.circle
                      cx={pos.x}
                      cy={pos.y}
                      r={INNER_DOT_R}
                      fill={deg === 90 || deg === 270 ? accent : primary}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{
                        scale: 1,
                        opacity: 1,
                        transition: { duration: 0.35, ease: [0.22, 0.61, 0.36, 1] },
                      }}
                    />
                  </g>
                );
              })}
            </g>
          )}

          {/* Phase 2: outer circle grows and group revolves. Shown when outerNodeRadius > 0. */}
          {outerNodeRadius > 0 && (
            <g transform={`translate(${cx}, ${cy})`}>
              <motion.g style={{ rotate: rotationMotion }}>
                {[0, 90, 180, 270].map((deg) => {
                  const pos = polarToCart(0, 0, ORBIT_R, deg);
                  const spokePath = `M 0 0 L ${pos.x} ${pos.y}`;
                  return (
                    <path
                      key={`p2-spoke-${deg}`}
                      d={spokePath}
                      fill="none"
                      stroke="url(#line-grad)"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      opacity={0.6}
                      style={{ filter: 'url(#glow)' }}
                    />
                  );
                })}
                {NODES.map((node, i) => {
                  const deg = 90 * i;
                  const pos = polarToCart(0, 0, ORBIT_R, deg);
                  const isAccent = i % 2 === 1;
                  return (
                    <g key={node.key} transform={`translate(${pos.x}, ${pos.y})`}>
                      <circle
                        r={outerNodeRadius}
                        fill={isAccent ? accent : primary}
                        stroke={isDark ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.7)'}
                        strokeWidth="2.5"
                      />
                      <motion.g style={{ rotate: counterRotateMotion }}>
                        <foreignObject
                          x={-ORBIT_NODE_R * 0.7}
                          y={-ORBIT_NODE_R * 0.7}
                          width={ORBIT_NODE_R * 1.4}
                          height={ORBIT_NODE_R * 1.4}
                          style={{ overflow: 'visible' }}
                        >
                          <div className="flex h-full w-full flex-col items-center justify-center p-1 box-border gap-0.5">
                            <Icon icon={node.icon} width="var(--loader-icon-size)" height="var(--loader-icon-size)" style={{ color: '#fff' }} className="flex-shrink-0" />
                            <span className="text-white text-[10px] sm:text-xs font-semibold leading-tight text-center" style={{ fontFamily: 'Poppins, sans-serif' }}>
                              {t(node.labelKey)}
                            </span>
                          </div>
                        </foreignObject>
                      </motion.g>
                    </g>
                  );
                })}
              </motion.g>
            </g>
          )}
        </svg>
      </div>
    </div>
  );
}

export default AgentCircuitLoader;
